import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon, type IconName } from './Icon';
import s from './Segmented.module.css';

export type SegmentedOption<V extends string | number> = {
  value: V;
  label: ReactNode;
  icon?: IconName;
  badge?: ReactNode;
  disabled?: boolean;
};

type Props<V extends string | number> = {
  options: SegmentedOption<V>[];
  value: V;
  onChange: (value: V) => void;
  /** header = RU/UZ; filter = page filters; form = in modals; compact = drawers; tabs = org/site tabs. */
  variant?: 'header' | 'filter' | 'form' | 'compact' | 'tabs';
  /** Accessible name of the group. */
  label: string;
  className?: string;
};

/**
 * One control for periods, filters, tabs, language, size, style…: a radiogroup (or a tablist for
 * `tabs`) with a roving tab stop. Arrow keys, Home and End move and select; disabled options are skipped.
 */
export function Segmented<V extends string | number>({
  options,
  value,
  onChange,
  variant = 'filter',
  label,
  className,
}: Props<V>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabs = variant === 'tabs';
  const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
  const current = options.findIndex((o) => o.value === value);
  const focusable = current >= 0 && !options[current]?.disabled ? current : (enabled[0] ?? -1);

  const move = (to: number) => {
    const opt = options[to];
    if (!opt) return;
    refs.current[to]?.focus();
    if (opt.value !== value) onChange(opt.value);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!enabled.length) return;
    const pos = Math.max(0, enabled.indexOf(current));
    let next: number | undefined;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = enabled[(pos + 1) % enabled.length];
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = enabled[(pos - 1 + enabled.length) % enabled.length];
    else if (e.key === 'Home') next = enabled[0];
    else if (e.key === 'End') next = enabled[enabled.length - 1];
    if (next === undefined) return;
    e.preventDefault();
    move(next);
  };

  return (
    <div
      role={tabs ? 'tablist' : 'radiogroup'}
      aria-label={label}
      className={cx(s.track, s[variant], tabs && options.some((o) => o.icon) && s.withIcons, className)}
      onKeyDown={onKeyDown}
    >
      {options.map((o, i) => {
        const on = o.value === value;
        return (
          <button
            key={String(o.value)}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role={tabs ? 'tab' : 'radio'}
            aria-checked={tabs ? undefined : on}
            aria-selected={tabs ? on : undefined}
            tabIndex={i === focusable ? 0 : -1}
            disabled={o.disabled}
            className={s.seg}
            onClick={() => !on && onChange(o.value)}
          >
            {o.icon && <Icon name={o.icon} size={14} />}
            {o.label}
            {o.badge !== undefined && o.badge !== null && <span className={s.badge}>{o.badge}</span>}
          </button>
        );
      })}
    </div>
  );
}
